// FILENAME: frontend/src/components/DetailPanel.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import 'react18-json-view/src/style.css';
import '../styles/jsonThemes.css';
import { useAuth } from '../auth/googleDomainAuth.jsx';
import useApi from '../hooks/useApi';
import {
  getApiPresenceStatusMeta,
  getEnrollmentStatusMeta,
  getProductSelectionStatusMeta,
  getHasLiveSelectionsMeta,
  getConsentStatusMeta,
  getTerminationStatusMeta,
} from '../utils/status';
import {
  isEnrollmentTerminated,
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../utils/enrollmentEvidence';
import { buildSearchMatchPayload } from '../utils/searchHighlights';
import { recordSuccessfulCall, getAverageDuration, getMaxDuration } from '../utils/bacTimingStorage';
import { formatDate } from './detailPanel/utils/dates';
import { rowLooksLikeSelectionContext } from './detailPanel/utils/rowContext';
import { buildStatusChip } from './detailPanel/utils/statusChip';
import { mergeDealerPayload as mergeDealerPayloadImpl } from './detailPanel/utils/mergeDealerPayload';
import buildDealerContext from './detailPanel/utils/buildDealerContext';
import { useDetailPanelRowState } from './detailPanel/hooks/useDetailPanelRowState';
import useDetailPanelNotify from './detailPanel/hooks/useDetailPanelNotify';
import { useSingleDealerRefresh } from './detailPanel/hooks/useSingleDealerRefresh';
import DetailPanelTabs from './detailPanel/components/DetailPanelTabs';
import {
  DETAIL_PANEL_TAB_DEALER_360,
} from './detailPanel/constants/detailPanelTabs';
import DetailPanelActions from './detailPanel/components/DetailPanelActions';
import DetailPanelContent from './detailPanel/components/DetailPanelContent';
import useDetailPanelSelectionsModel from './detailPanel/hooks/useDetailPanelSelectionsModel';

const hasHydratedSelections = (payload) => Array.isArray(payload?.productSelections);

const DetailPanel = ({
  rowData,
  onNavigateToDealer = () => {},
  onRowUpdated = () => {},
  autoHydrateSelections = true,
}) => {
  const { user } = useAuth() || {};
  const {
    currentRow,
    setCurrentRow,
    activeTab,
    setActiveTab,
    selectedSelectionIndex,
    setSelectedSelectionIndex,
    jsonCollapsed,
    setJsonCollapsed,
    selectionKeyTargetRef,
    autoHydrateKeyRef,
    programKey,
    programCode,
    programLabel,
  } = useDetailPanelRowState(rowData);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    onClick: null,
    key: 0,
  });
  const [refreshProgress, setRefreshProgress] = useState({
    isActive: false,
    progress: 0,
    startTime: null,
    estimatedDuration: null,
  });
  const { useForceProgramSync, useRefreshDealerSelections, useGetCacheStatus, useDiOrderItemsByReferenceId } = useApi();
  const { mutateAsync: refreshProgram, isPending: isProgramRefreshing } = useForceProgramSync();
  const { mutateAsync: refreshDealerSelections, isPending: isDealerRefreshing } = useRefreshDealerSelections();
  const { data: cacheStatus } = useGetCacheStatus();
  const { mutateAsync: fetchDiOrderItemsByReferenceId } = useDiOrderItemsByReferenceId();
  const autoHydrateInFlightRef = useRef(new Set());
  const autoHydratedKeysRef = useRef(new Set());

  const handleSnackbarClose = useCallback((_event, reason) => {
    // MUI fires `clickaway` for the same click that triggers the toast in some layouts.
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);
  const isGlobalRefreshInProgress = cacheStatus?.snapshot?.status === 'in_progress';

  // Update progress bar every 300ms when refresh is active
  useEffect(() => {
    if (!refreshProgress.isActive || !refreshProgress.startTime || !refreshProgress.estimatedDuration) {
      return;
    }

    const interval = setInterval(() => {
      setRefreshProgress((prev) => {
        if (!prev.isActive || !prev.startTime || !prev.estimatedDuration) {
          return prev;
        }
        const elapsed = Date.now() - prev.startTime;
        // If elapsed time exceeds estimate, update estimate to current elapsed + buffer
        let currentEstimate = prev.estimatedDuration;
        if (elapsed > currentEstimate) {
          currentEstimate = elapsed + 1000; // Add 1 second buffer
        }
        const progressPercent = Math.min((elapsed / currentEstimate) * 100, 90); // Cap at 90% until completion
        return {
          ...prev,
          progress: progressPercent,
          estimatedDuration: currentEstimate,
        };
      });
    }, 300);

    return () => clearInterval(interval);
  }, [refreshProgress.isActive, refreshProgress.startTime, refreshProgress.estimatedDuration]);

  // Merge a fresh dealer payload into the drawer state while preserving derived flags.
  // (Defined early because post-send refresh helpers depend on it.)
  const mergeDealerPayload = useCallback(
    (prev, payload) => mergeDealerPayloadImpl(prev, payload),
    [],
  );

  // If a caller provided a nested enrollment payload (e.g. Status Changes rows include `sourceEnrollment`),
  // opportunistically seed the drawer with its selections/vendors so we can render immediately without a fetch.
  useEffect(() => {
    if (!currentRow) return;
    const source = currentRow?.sourceEnrollment;
    if (!source) return;
    const sourceSelections = Array.isArray(source?.productSelections) ? source.productSelections : null;
    const sourceVendors = Array.isArray(source?.vendors) ? source.vendors : null;
    if ((!sourceSelections || sourceSelections.length === 0) && (!sourceVendors || sourceVendors.length === 0)) return;

    const alreadyHasSelections =
      Array.isArray(currentRow?.productSelections) && currentRow.productSelections.length > 0;
    const alreadyHasVendors = Array.isArray(currentRow?.vendors) && currentRow.vendors.length > 0;
    if (alreadyHasSelections && alreadyHasVendors) return;

    setCurrentRow((prev) => {
      const prevHasSelections = Array.isArray(prev?.productSelections) && prev.productSelections.length > 0;
      const prevHasVendors = Array.isArray(prev?.vendors) && prev.vendors.length > 0;
      if (prevHasSelections && prevHasVendors) return prev;
      return mergeDealerPayload(prev, source);
    });
  }, [currentRow, mergeDealerPayload, setCurrentRow]);

  // Auto-hydrate the detail panel when the source row doesn't include the full dealer payload.
  // Common case: report rows have BAC/program metadata but omit `productSelections` and `vendors`.
  // Prefer the cached single-dealer record first, but allow the dedicated single-BAC endpoint to
  // fall through to a live one-dealer refresh on cache miss. Avoid cacheOnly here so the drawer
  // can hydrate without forcing callers back through a full program payload fetch.
  useEffect(() => {
    if (!currentRow || !autoHydrateSelections) return undefined;

    const bac = currentRow?.bac != null ? String(currentRow.bac).trim() : '';
    if (!bac) return undefined;

    // If the caller explicitly provided a selections array, respect non-empty arrays.
    // But if it's an empty placeholder and the row looks like a selection-context row,
    // hydrate anyway (common for Status Changes / selection-level tables).
    if (Array.isArray(currentRow?.productSelections)) {
      if (currentRow.productSelections.length > 0) return undefined;
      if (!rowLooksLikeSelectionContext(currentRow)) return undefined;
    }

    const programNameForRefresh = programKey || '';
    if (!programNameForRefresh) return undefined;

    const hydrateKey = `${programNameForRefresh}:${bac}`;
    if (autoHydratedKeysRef.current.has(hydrateKey)) return undefined;
    if (autoHydrateInFlightRef.current.has(hydrateKey)) {
      return undefined;
    }
    autoHydrateInFlightRef.current.add(hydrateKey);
    autoHydrateKeyRef.current = hydrateKey;
    (async () => {
      try {
        let payload = await refreshDealerSelections({
          programName: programNameForRefresh,
          bac,
          preferCache: true,
        });
        if (autoHydrateKeyRef.current !== hydrateKey) return;
        const rowAlreadyHasSelections =
          Array.isArray(currentRow?.productSelections) && currentRow.productSelections.length > 0;
        if (!rowAlreadyHasSelections && !hasHydratedSelections(payload)) {
          payload = await refreshDealerSelections({
            programName: programNameForRefresh,
            bac,
          });
          if (autoHydrateKeyRef.current !== hydrateKey) return;
        }
        if (!payload) return;
        autoHydratedKeysRef.current.add(hydrateKey);

        let merged;
        setCurrentRow((prev) => {
          // Guard against the row switching while the request was in-flight.
          const prevBac = prev?.bac != null ? String(prev.bac).trim() : '';
          if (prevBac !== bac) return prev;
          merged = mergeDealerPayload(prev, payload);
          return merged;
        });
        if (merged) {
          // Avoid state updates during render warnings in parent components.
          setTimeout(() => onRowUpdated(merged), 0);
        }
      } catch (err) {
        // Silent: user can always hit "Refresh Data" if hydration fails.
        console.warn('[DetailPanel] auto-hydrate failed', {
          bac,
          programName: programNameForRefresh,
          message: err?.response?.data?.message || err?.message,
        });
      } finally {
        autoHydrateInFlightRef.current.delete(hydrateKey);
      }
    })();

    return undefined;
  }, [
    currentRow,
    autoHydrateSelections,
    programKey,
    autoHydrateKeyRef,
    refreshDealerSelections,
    mergeDealerPayload,
    onRowUpdated,
    setCurrentRow,
  ]);

  const activeDealerRefreshKey = useMemo(() => {
    const activeProgram = programKey || '';
    const activeBac = currentRow?.bac || '';
    if (!activeProgram || !activeBac) return '';
    return `${activeProgram}:${String(activeBac)}`;
  }, [currentRow?.bac, programKey]);

  const { autoRefreshInFlight, requestSingleDealerRefresh } = useSingleDealerRefresh({
    activeDealerRefreshKey,
    isGlobalRefreshInProgress,
    refreshDealerSelections,
    mergeDealerPayload,
    setCurrentRow,
    onRowUpdated,
    setSnackbar,
    setRefreshProgress,
    getAverageDuration,
    getMaxDuration,
    recordSuccessfulCall,
  });

  const isRefreshing = isProgramRefreshing || isDealerRefreshing || autoRefreshInFlight;

  const handleNavigate = () => {
    if (!currentRow?.bac) return;
    onNavigateToDealer(currentRow.bac, {
      searchMatch: buildSearchMatchPayload(currentRow),
    });
  };

  // Refresh only this dealer/program pair; fall back to program-wide refresh if it fails.
  const handleRefreshProgram = async () => {
    const programNameForRefresh = programKey;
    if (!programNameForRefresh) {
      setSnackbar({
        open: true,
        message: 'Program name is unavailable; cannot refresh.',
        severity: 'error',
      });
      return;
    }

    if (!currentRow?.bac) {
      setSnackbar({
        open: true,
        message: 'Dealer BAC is unavailable; cannot refresh.',
        severity: 'error',
      });
      return;
    }

    console.info('[DetailPanel] single-dealer refresh requested', {
      programName: programNameForRefresh,
      bac: currentRow.bac,
    });

    // Initialize progress bar
    const startTime = Date.now();
    const averageDuration = getAverageDuration();
    setRefreshProgress({
      isActive: true,
      progress: 0,
      startTime,
      estimatedDuration: averageDuration,
    });

    try {
      // Manual refresh bypasses cooldown/backoff but still dedupes if a refresh is already in-flight.
      const payload =
        (await requestSingleDealerRefresh({
          programName: programNameForRefresh,
          bac: currentRow.bac,
          delayMs: 0,
          force: true,
        })) || (await refreshDealerSelections({ programName: programNameForRefresh, bac: currentRow.bac }));
      if (!payload) {
        throw new Error('Dealer refresh returned no data.');
      }

      // Calculate actual duration and store if successful
      const actualDuration = Date.now() - startTime;
      
      // Update estimate if call exceeded average
      const maxDuration = getMaxDuration();
      const updatedEstimate = Math.max(averageDuration, maxDuration, actualDuration);
      
      // Move progress to 100% on completion
      setRefreshProgress((prev) => ({
        ...prev,
        progress: 100,
        estimatedDuration: updatedEstimate,
      }));

      // Store successful call timing
      recordSuccessfulCall(actualDuration);

      // Hide progress bar after brief delay
      setTimeout(() => {
        setRefreshProgress({
          isActive: false,
          progress: 0,
          startTime: null,
          estimatedDuration: null,
        });
      }, 500);

      const merged = mergeDealerPayload(currentRow, payload);
      setCurrentRow(merged);
      // Call onRowUpdated after state update to avoid render warning
      onRowUpdated(merged);

      setSnackbar({
        open: true,
        message: 'Dealer selections refreshed.',
        severity: 'success',
      });
    } catch (error) {
      // Hide progress bar on error (don't store failed call timing)
      setRefreshProgress({
        isActive: false,
        progress: 0,
        startTime: null,
        estimatedDuration: null,
      });
      const baseMessage = error?.response?.data?.message || error?.message || 'Failed to refresh dealer selections.';
      setSnackbar({
        open: true,
        message: `${baseMessage} Running full program refresh...`,
        severity: 'warning',
      });

      try {
        const result = await refreshProgram(programKey);
        if (result?.queued) {
          setSnackbar({
            open: true,
            message: result?.message || `Queued refresh for ${programKey}; will run after the active global sync completes.`,
            severity: 'info',
          });
          return;
        }
        const updatedRow = Array.isArray(result?.data)
          ? result.data.find((item) => item.bac === currentRow.bac)
          : null;

        if (updatedRow) {
          const mergedRow = { ...currentRow, ...updatedRow };
          mergedRow._original = updatedRow._original ?? mergedRow._original;
          if (Array.isArray(mergedRow.productSelections)) {
            mergedRow.hasLiveSelections = mergedRow.productSelections.some(
              (selection) => (selection?.productSelectionStatus || '').toUpperCase() === 'LIVE'
            );
          }
          setCurrentRow(mergedRow);
          onRowUpdated(mergedRow);
        }

        setSnackbar({
          open: true,
          message: `Program data refreshed for ${programKey}.`,
          severity: 'success',
        });
      } catch (fallbackError) {
        const message = fallbackError?.response?.data?.message || fallbackError?.message || 'Failed to refresh program data.';
        setSnackbar({
          open: true,
          message,
          severity: 'error',
        });
      }
    }
  };

  const { rawSelections, productSelections, handleDetailPaneSelectionRowClick } = useDetailPanelSelectionsModel({
    currentRow,
    programCode,
    programKey,
    selectionKeyTargetRef,
    selectedSelectionIndex,
    setSelectedSelectionIndex,
    setActiveTab,
    fetchDiOrderItemsByReferenceId,
    setSnackbar,
  });

  const enrollmentStatusMeta = getEnrollmentStatusMeta(resolveEnrollmentLifecycleStatus(currentRow));
  const apiPresenceStatusMeta = getApiPresenceStatusMeta(resolveApiPresenceStatus(currentRow));
  const productStatusMeta = getProductSelectionStatusMeta(currentRow?.productSelectionStatusCd);
  const terminationStatusMeta = getTerminationStatusMeta(isEnrollmentTerminated(currentRow));
  const liveSelectionsMeta = getHasLiveSelectionsMeta(currentRow?.hasLiveSelections);
  const programConsentMeta = getConsentStatusMeta(currentRow?.programConsentStatusCd);
  const dataConsentMeta = getConsentStatusMeta(currentRow?.dataConsentStatusCd);

  const dealerContext = useMemo(() => buildDealerContext(currentRow), [currentRow]);

  const handleTabChange = (_event, newValue) => {
    if (newValue === DETAIL_PANEL_TAB_DEALER_360) {
      handleNavigate();
      return;
    }
    setActiveTab(newValue);
  };

  const handleSnackbarNavigate = useCallback(() => {
    setActiveTab(3);
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, [setActiveTab]);

  const notifyFromSendTab = useDetailPanelNotify({
    handleSnackbarNavigate,
    programKey,
    currentBac: currentRow?.bac,
    requestSingleDealerRefresh,
    setSnackbar,
  });

  if (!currentRow) {
    return <Typography>Select a row to see details.</Typography>;
  }

  const isHydratingDetails =
    Boolean(currentRow?.bac) &&
    Boolean(programKey) &&
    (
      autoHydrateInFlightRef.current.has(`${programKey}:${String(currentRow?.bac || '').trim()}`) ||
      !Array.isArray(currentRow?.productSelections)
    );

  return (
    <>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DetailPanelTabs value={activeTab} onChange={handleTabChange} />
        <DetailPanelContent
          activeTab={activeTab}
          currentRow={currentRow}
          dealerContext={dealerContext}
          userEmail={user?.email || null}
          programLabel={programLabel}
          programCode={programCode}
          programKey={programKey}
          isHydratingDetails={isHydratingDetails}
          isRefreshing={isRefreshing}
          jsonCollapsed={jsonCollapsed}
          setJsonCollapsed={setJsonCollapsed}
          rawSelections={rawSelections}
          productSelections={productSelections}
          selectedSelectionIndex={selectedSelectionIndex}
          setSelectedSelectionIndex={setSelectedSelectionIndex}
          setActiveTab={setActiveTab}
          notifyFromSendTab={notifyFromSendTab}
          handleDetailPaneSelectionRowClick={handleDetailPaneSelectionRowClick}
          handleRefreshProgram={handleRefreshProgram}
          onNavigateToDealer={onNavigateToDealer}
          enrollmentStatusMeta={enrollmentStatusMeta}
          apiPresenceStatusMeta={apiPresenceStatusMeta}
          productStatusMeta={productStatusMeta}
          terminationStatusMeta={terminationStatusMeta}
          liveSelectionsMeta={liveSelectionsMeta}
          programConsentMeta={programConsentMeta}
          dataConsentMeta={dataConsentMeta}
          buildStatusChip={buildStatusChip}
          formatDate={formatDate}
        />
        <DetailPanelActions refreshProgress={refreshProgress} snackbar={snackbar} onSnackbarClose={handleSnackbarClose} />
      </Box>
    </>
  );
};

export default DetailPanel;
